// ============================================================================
// 📦 VERSION: 2026.01.28.1400
// ============================================================================
// QuizLobby.jsx - Group Quiz Lobby / Waiting Room
// Players wait here until quiz starts. Shows countdown, player list, share options.
// Settings controlled by: Admin (ads) + QuizMaster (sharing)
// ============================================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = 'https://api.traktag.com/api';

const QuizLobby = ({
  playerData,
  adminSettings: propAdminSettings,
  onQuizStart
}) => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const quizCode = code || searchParams.get('code') || 'TEST123';
  const username = playerData?.username || searchParams.get('user') || 'Player';
  const playerId = playerData?.id || searchParams.get('pid') || null;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizData, setQuizData] = useState({
    title: 'Group Quiz',
    icon: '🎪',
    questionCount: 20,
    category: 'General Knowledge',
    allowSharing: true,
    startTime: null,
    isHost: false
  });
  const [participants, setParticipants] = useState([]);
  const [countdown, setCountdown] = useState('--:--');
  const [countdownUrgency, setCountdownUrgency] = useState('normal');
  const [copySuccess, setCopySuccess] = useState(false);

  // Admin settings (passed as prop or loaded)
  const [adminSettings, setAdminSettings] = useState({
    adsEnabled: true,
    adPages: { lobby: true },
    adFormats: { '320x50': true, '300x250': true },
    debugMode: true,
    maintenanceMode: false,
    ...propAdminSettings
  });

  // Load admin settings
  useEffect(() => {
    const loadAdminSettings = async () => {
      try {
        // Try API first
        const response = await fetch(`${API_URL}/admin/settings`);
        if (response.ok) {
          const data = await response.json();
          setAdminSettings(prev => ({ ...prev, ...data }));
        }
      } catch (err) {
        // Fallback to localStorage
        const stored = localStorage.getItem('traktag_admin_settings');
        if (stored) {
          setAdminSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
        }
      }
    };
    loadAdminSettings();
  }, []);

  // Load quiz data
  useEffect(() => {
    const loadQuizData = async () => {
      try {
        setLoading(true);
        
        // Try API first
        const response = await fetch(`${API_URL}/group-quiz/${quizCode}`);
        if (response.ok) {
          const data = await response.json();
          setQuizData(prev => ({ ...prev, ...data }));
        } else {
          // Fallback to localStorage for testing
          const stored = localStorage.getItem(`quiz_${quizCode}`);
          if (stored) {
            setQuizData(prev => ({ ...prev, ...JSON.parse(stored) }));
          }
        }
      } catch (err) {
        console.error('Error loading quiz:', err);
        // Fallback to localStorage
        const stored = localStorage.getItem(`quiz_${quizCode}`);
        if (stored) {
          setQuizData(prev => ({ ...prev, ...JSON.parse(stored) }));
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (quizCode) {
      loadQuizData();
    }
  }, [quizCode]);

  // Load participants
  const loadParticipants = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/group-quiz/${quizCode}/participants`);
      if (response.ok) {
        const data = await response.json();
        setParticipants(data);
      }
    } catch (err) {
      // Fallback to localStorage
      const stored = localStorage.getItem(`participants_${quizCode}`);
      if (stored) {
        setParticipants(JSON.parse(stored));
      } else {
        // Demo players
        setParticipants([
          { username, displayName: username, avatar: '😎', team: '' },
          { username: 'QuizMaster', displayName: 'QuizMaster', avatar: '🎯', team: '' },
          { username: 'Brainiac', displayName: 'Brainiac', avatar: '🧠', team: 'Team Alpha' },
          { username: 'TriviaKing', displayName: 'TriviaKing', avatar: '👑', team: 'Team Alpha' },
        ]);
      }
    }
  }, [quizCode, username]);

  // Poll for participant updates
  useEffect(() => {
    loadParticipants();
    const interval = setInterval(loadParticipants, 3000);
    return () => clearInterval(interval);
  }, [loadParticipants]);

  // Countdown timer
  useEffect(() => {
    const targetTime = quizData.startTime || (Date.now() + 30 * 1000);
    
    const tick = () => {
      const now = Date.now();
      const remainingMs = Math.max(0, targetTime - now);
      const remainingSecs = Math.floor(remainingMs / 1000);
      
      // Format countdown
      const days = Math.floor(remainingSecs / 86400);
      const hours = Math.floor((remainingSecs % 86400) / 3600);
      const minutes = Math.floor((remainingSecs % 3600) / 60);
      const seconds = remainingSecs % 60;
      
      let display;
      if (days > 0) {
        display = `${days}d ${hours}h ${minutes}m ${seconds}s`;
      } else if (hours > 0) {
        display = `${hours}h ${minutes}m ${seconds}s`;
      } else {
        display = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      
      setCountdown(display);
      
      // Set urgency
      if (remainingSecs <= 10) {
        setCountdownUrgency('urgent');
      } else if (remainingSecs <= 60) {
        setCountdownUrgency('warning');
      } else {
        setCountdownUrgency('normal');
      }
      
      // Start quiz when countdown reaches zero
      if (remainingSecs <= 0) {
        handleStartQuiz();
        return;
      }
    };
    
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [quizData.startTime]);

  // Handlers
  const handleStartQuiz = () => {
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    
    if (onQuizStart) {
      onQuizStart(quizCode);
    } else {
      navigate(`/group-quiz/play/${quizCode}?user=${encodeURIComponent(username)}${playerId ? `&pid=${playerId}` : ''}`);
    }
  };

  const handleLeaveLobby = () => {
    if (window.confirm('Leave this quiz lobby?')) {
      // Remove from participants
      const updated = participants.filter(p => p.username !== username);
      localStorage.setItem(`participants_${quizCode}`, JSON.stringify(updated));
      
      // API call to leave
      fetch(`${API_URL}/group-quiz/${quizCode}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, playerId })
      }).catch(() => {});
      
      navigate('/');
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(quizCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleShare = async () => {
    const shareText = `Join my quiz "${quizData.title}" on TRAKtag!\n\nCode: ${quizCode}\nPlay at: groups.traktag.com/join`;
    const shareUrl = `https://groups.traktag.com/join?code=${quizCode}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join: ${quizData.title}`,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      alert('Quiz details copied to clipboard!');
    }
  };

  const handleInvite = async () => {
    const inviteUrl = `https://groups.traktag.com/join?code=${quizCode}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join: ${quizData.title}`,
          text: `Join my quiz! Code: ${quizCode}`,
          url: inviteUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(inviteUrl);
      alert(`Link copied!\n\n${inviteUrl}`);
    }
  };

  // Derived state
  const showAds = adminSettings.adsEnabled && adminSettings.adPages?.lobby;
  const showSharing = quizData.allowSharing !== false;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center p-4">
        <div className="text-white text-xl">Loading lobby...</div>
      </div>
    );
  }

  // Maintenance mode
  if (adminSettings.maintenanceMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 max-w-[340px] text-center">
          <div className="text-4xl mb-4">🔧</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">Under Maintenance</h1>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 flex items-center justify-center p-4">
        <div className="text-red-300 text-xl">Error: {error}</div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-900 p-4">
      {/* Top Ad Banner - Admin controlled */}
      {showAds && adminSettings.adFormats?.['320x50'] && (
        <div className="flex justify-center mb-4">
          <div className="bg-gray-100 rounded-lg p-2">
            <div className="bg-gray-200 rounded w-[320px] h-[50px] flex items-center justify-center text-gray-500 text-sm">
              Ad 320×50
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="max-w-[400px] mx-auto bg-white rounded-2xl p-6 shadow-xl">
        {/* Quiz Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">{quizData.icon || '🎪'}</div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">
            {quizData.title || 'Group Quiz'}
          </h1>
          <p className="text-gray-500 text-sm mb-4">
            {quizData.questionCount || '--'} questions • {quizData.category || '--'}
          </p>
          <div className="inline-block bg-gradient-to-r from-purple-600 to-purple-800 text-white px-5 py-2 rounded-full font-bold text-sm">
            {participants.length} Players Ready
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="text-center mb-6">
          <div className={`
            inline-block text-3xl font-bold px-6 py-4 rounded-xl bg-gray-100 min-w-[200px]
            ${countdownUrgency === 'urgent' ? 'text-red-500 animate-pulse' : ''}
            ${countdownUrgency === 'warning' ? 'text-yellow-500' : ''}
            ${countdownUrgency === 'normal' ? 'text-green-500' : ''}
          `}>
            {countdown}
          </div>
          <p className="text-gray-500 text-sm mt-2">
            Quiz starts when timer reaches zero
          </p>
        </div>

        {/* Ad Rectangle 1 - Admin controlled */}
        {showAds && adminSettings.adFormats?.['300x250'] && (
          <div className="flex justify-center mb-5">
            <div className="bg-gray-100 rounded-lg p-2">
              <div className="bg-gray-200 rounded w-[300px] h-[250px] flex items-center justify-center text-gray-500 text-sm">
                Ad 300×250
              </div>
            </div>
          </div>
        )}

        {/* Quiz Code - QuizMaster controlled */}
        {showSharing && (
          <div className="bg-green-50 border-2 border-dashed border-green-400 rounded-xl p-4 mb-5 text-center">
            <p className="text-gray-500 text-xs mb-1">Quiz Code</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl font-bold text-green-600 tracking-widest font-mono">
                {quizCode}
              </span>
              <button
                onClick={handleCopyCode}
                className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded-full transition-all"
              >
                {copySuccess ? '✅ Copied!' : '📋 Copy'}
              </button>
            </div>
          </div>
        )}

        {/* Pro Tip - QuizMaster controlled */}
        {showSharing && (
          <div className="bg-yellow-50 border-2 border-dashed border-yellow-400 rounded-xl p-4 mb-5 text-center">
            <p className="text-yellow-700 font-semibold text-sm mb-1">💡 Pro Tip</p>
            <p className="text-yellow-600 text-sm">
              More players = more competition! Share the quiz code with friends.
            </p>
          </div>
        )}

        {/* Players Grid */}
        <div className="mb-5">
          <h3 className="text-center text-gray-700 font-semibold mb-3">
            👥 Players in Lobby
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {participants.map((player, idx) => (
              <div
                key={player.username || idx}
                className={`
                  text-center p-3 rounded-xl transition-all
                  ${player.username === username 
                    ? 'bg-green-50 border-2 border-green-400' 
                    : 'bg-gray-100 border-2 border-transparent'}
                `}
              >
                <div className="text-2xl mb-1">{player.avatar || '😀'}</div>
                <div className="text-xs font-semibold text-gray-700 truncate">
                  {player.displayName || player.username}
                </div>
                {player.team && (
                  <div className="text-[10px] text-gray-500 truncate">
                    {player.team}
                  </div>
                )}
                {player.username === username && (
                  <span className="inline-block bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full mt-1">
                    You
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Social Actions - QuizMaster controlled */}
        {showSharing && (
          <div className="flex gap-3 justify-center mb-5">
            <button
              onClick={handleShare}
              className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all"
            >
              📤 Share Quiz
            </button>
            <button
              onClick={handleInvite}
              className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-4 py-2 rounded-full text-sm font-semibold transition-all"
            >
              👥 Invite Friends
            </button>
          </div>
        )}

        {/* Ad Rectangle 2 - Admin controlled */}
        {showAds && adminSettings.adFormats?.['300x250'] && (
          <div className="flex justify-center mb-5">
            <div className="bg-gray-100 rounded-lg p-2">
              <div className="bg-gray-200 rounded w-[300px] h-[250px] flex items-center justify-center text-gray-500 text-sm">
                Ad 300×250
              </div>
            </div>
          </div>
        )}

        {/* Bottom Actions */}
        <div className="flex justify-center gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleLeaveLobby}
            className="bg-gray-500 hover:bg-gray-600 text-white px-5 py-3 rounded-full font-semibold transition-all"
          >
            ← Leave
          </button>
          {quizData.isHost && (
            <button
              onClick={handleStartQuiz}
              className="bg-green-500 hover:bg-green-600 text-white px-5 py-3 rounded-full font-semibold transition-all"
            >
              ▶ Start Now
            </button>
          )}
        </div>
      </div>

      {/* Version Badge - Debug mode only */}
      {adminSettings.debugMode && (
        <div className="fixed bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          QuizLobby.jsx v2026.01.28.1400
        </div>
      )}
    </div>
  );
};

export default QuizLobby;
